// @ts-nocheck
// Store Custom Code
import React, { FC, useMemo, useState } from "react";
import { css } from "styled-components";

import Zone from "@insite/client-framework/Components/Zone";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Icon from "@insite/mobius/Icon";
import Modal from "@insite/mobius/Modal";
import Popover from "@insite/mobius/Popover";
import getColor from "@insite/mobius/utilities/getColor";
import { iconActionButtonStyles } from "@store/Themes/common/mixins/iconButton";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import * as cssLinter from "css";

const enum fields {
    triggerType = "triggerType",
    buttonLabel = "buttonLabel",
    iconName = "iconName",
    displayMode = "displayMode",
    modalTitle = "modalTitle",
    modalSize = "modalSize",
    popoverXPosition = "popoverXPosition",
    popoverYPosition = "popoverYPosition",
    customCSS = "customCSS",
}

interface Props extends WidgetProps {
    fields: {
        [fields.triggerType]: "button" | "icon" | "both";
        [fields.buttonLabel]: string;
        [fields.iconName]: string;
        [fields.displayMode]: "modal" | "popover";
        [fields.modalTitle]: string;
        [fields.modalSize]: "small" | "medium" | "large";
        [fields.popoverXPosition]: "start" | "middle" | "end";
        [fields.popoverYPosition]: "top" | "bottom";
        [fields.customCSS]: string;
    };
}

const InfoModal: FC<Props> = ({
    id,
    fields: {
        triggerType,
        buttonLabel,
        iconName,
        displayMode,
        modalTitle,
        modalSize,
        popoverXPosition,
        popoverYPosition,
        customCSS,
    },
}) => {
    const [isOpen, setIsOpen] = useState(false);

    const styles = useMemo(() => {
        return {
            wrapper: {
                css: css`
                    ${customCSS}
                `,
            },
            content: {
                css: css`
                    min-height: 1px;
                `,
            },
            buttonTrigger: {
                css: css`
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: 0.5rem;
                    min-height: ${pxToRem(34)};
                    padding: ${pxToRem(8, 16)};
                    border: 2px solid ${getColor("primary")};
                    border-radius: 0.25rem;
                    background: ${getColor("primary")};
                    color: ${getColor("common.background")};
                    cursor: pointer;
                    font: inherit;
                `,
            },
            iconButton: {
                css: css`
                    ${iconActionButtonStyles()}
                `,
            },
        };
    }, [customCSS]);

    // "info" was the legacy free-text default before the icon picker; it isn't a real icon key.
    const iconSrc = !iconName || iconName === "info" ? "InfoLine" : iconName;

    const renderTrigger = () => {
        const handleClick = () => {
            if (displayMode === "modal") {
                setIsOpen(true);
            }
        };

        if (triggerType === "button") {
            return (
                <StyledWrapper
                    {...styles.buttonTrigger}
                    as="button"
                    type="button"
                    onClick={handleClick}
                    data-test-selector="infoModalTrigger"
                >
                    {buttonLabel}
                </StyledWrapper>
            );
        }

        if (triggerType === "icon") {
            return (
                <StyledWrapper
                    {...styles.iconButton}
                    as="button"
                    type="button"
                    onClick={handleClick}
                    aria-label={buttonLabel || modalTitle || "Info"}
                    title={buttonLabel || modalTitle || "Info"}
                    data-test-selector="infoModalTrigger"
                >
                    <Icon src={iconSrc} size={20} color="inherit" />
                </StyledWrapper>
            );
        }

        return (
            <StyledWrapper
                {...styles.buttonTrigger}
                as="button"
                type="button"
                onClick={handleClick}
                data-test-selector="infoModalTrigger"
            >
                {buttonLabel}
                <Icon src={iconSrc} size={18} />
            </StyledWrapper>
        );
    };

    const trigger = renderTrigger();

    if (displayMode === "modal") {
        return (
            <StyledWrapper {...styles.wrapper}>
                {trigger}
                <Modal
                    isOpen={isOpen}
                    handleClose={() => setIsOpen(false)}
                    headline={modalTitle}
                    sizeVariant={modalSize}
                >
                    <StyledWrapper {...styles.content} data-test-selector="infoModalContent">
                        <StyledWrapper>
                            <Zone contentId={id} zoneName="InfoModalContent" />
                        </StyledWrapper>
                    </StyledWrapper>
                </Modal>
            </StyledWrapper>
        );
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            <Popover
                popoverTrigger={trigger}
                xPosition={popoverXPosition}
                yPosition={popoverYPosition}
                contentBodyProps={{}}
                toggle
            >
                <StyledWrapper {...styles.content} data-test-selector="infoModalContent">
                    <StyledWrapper>
                        <Zone contentId={id} zoneName="InfoModalContent" />
                    </StyledWrapper>
                </StyledWrapper>
            </Popover>
        </StyledWrapper>
    );
};

const defaultCustomCss = `& {
}`;

const advancedTab = { displayName: "Advanced", sortOrder: 1 };

const widgetModule: WidgetModule<Bunzl.BunzlDefinition> = {
    component: InfoModal,
    definition: {
        group: "Basic",
        displayName: "Info Modal",
        icon: "info",
        fieldDefinitions: [
            {
                name: fields.triggerType,
                displayName: "Trigger type",
                fieldType: "General",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Button", value: "button" },
                    { displayName: "Icon", value: "icon" },
                    { displayName: "Button + Icon", value: "both" },
                ],
                defaultValue: "button",
                hideEmptyOption: true,
            },
            {
                name: fields.buttonLabel,
                displayName: "Button label",
                fieldType: "Translatable",
                editorTemplate: "TextField",
                defaultValue: "More info",
                isVisible: item => item.fields.triggerType !== "icon",
            },
            {
                name: fields.iconName,
                displayName: "Icon",
                fieldType: "General",
                editorTemplate: "IconPickerField",
                defaultValue: "InfoLine",
                isVisible: item => item.fields.triggerType !== "button",
            },
            {
                name: fields.displayMode,
                displayName: "Display mode",
                fieldType: "General",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Modal", value: "modal" },
                    { displayName: "Popover", value: "popover" },
                ],
                defaultValue: "modal",
                hideEmptyOption: true,
            },
            {
                name: fields.modalTitle,
                displayName: "Modal title",
                fieldType: "Translatable",
                editorTemplate: "TextField",
                defaultValue: "Info",
                isVisible: item => item.fields.displayMode === "modal",
            },
            {
                name: fields.modalSize,
                displayName: "Modal size",
                fieldType: "General",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Small", value: "small" },
                    { displayName: "Medium", value: "medium" },
                    { displayName: "Large", value: "large" },
                ],
                defaultValue: "medium",
                hideEmptyOption: true,
                isVisible: item => item.fields.displayMode === "modal",
            },
            {
                name: fields.popoverXPosition,
                displayName: "Popover X position",
                fieldType: "General",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Start", value: "start" },
                    { displayName: "Middle", value: "middle" },
                    { displayName: "End", value: "end" },
                ],
                defaultValue: "middle",
                hideEmptyOption: true,
                isVisible: item => item.fields.displayMode === "popover",
            },
            {
                name: fields.popoverYPosition,
                displayName: "Popover Y position",
                fieldType: "General",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Top", value: "top" },
                    { displayName: "Bottom", value: "bottom" },
                ],
                defaultValue: "bottom",
                hideEmptyOption: true,
                isVisible: item => item.fields.displayMode === "popover",
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
